<template>
  <div class="snapshot-manager">
    <a-space style="margin-bottom: 16px">
      <a-button type="primary" @click="createNewSnapshot">
        <template #icon><PlusOutlined /></template>
        创建新快照
      </a-button>
      <a-button @click="fetchSnapshots">
        <template #icon><ReloadOutlined /></template>
        刷新
      </a-button>
    </a-space>

    <a-table
      :columns="columns"
      :data-source="snapshots"
      :loading="loading"
      row-key="id"
      :pagination="false"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'snapshot_name'">
          <a-space>
            <span>{{ record.snapshot_name }}</span>
            <a-tag v-if="record.is_baseline" color="gold">基线</a-tag>
          </a-space>
        </template>
        <template v-else-if="column.key === 'snapshot_type'">
          <a-tag>{{ getTypeText(record.snapshot_type) }}</a-tag>
        </template>
        <template v-else-if="column.key === 'config_summary'">
          <div v-if="record.config_summary">
            <div>模型: {{ record.config_summary.model }}</div>
            <div>知识: {{ record.config_summary.knowledge_count }}条</div>
          </div>
        </template>
        <template v-else-if="column.key === 'action'">
          <a-space>
            <a-button type="link" size="small" @click="viewDetail(record.id)">查看</a-button>
            <a-button
              type="link"
              size="small"
              @click="setAsBaseline(record.id)"
              :disabled="!!record.is_baseline"
            >
              设为基线
            </a-button>
            <a-button
              type="link"
              size="small"
              @click="selectForCompare(record)"
              :disabled="compareList.length >= 2"
            >
              对比
            </a-button>
            <a-popconfirm
              title="确定要删除此快照吗？"
              @confirm="deleteSnapshot(record.id)"
            >
              <a-button type="link" size="small" danger>删除</a-button>
            </a-popconfirm>
          </a-space>
        </template>
      </template>
    </a-table>

    <!-- 对比快照选择 -->
    <div v-if="compareList.length > 0" style="margin-top: 16px">
      <a-alert
        type="info"
        :message="`已选择 ${compareList.length} 个快照用于对比`"
        closable
        @close="compareList = []"
      >
        <template #description>
          <a-space>
            <span v-for="item in compareList" :key="item.id">
              {{ item.snapshot_name }}
            </span>
            <a-button
              v-if="compareList.length === 2"
              type="primary"
              size="small"
              @click="compareSnapshots"
            >
              开始对比
            </a-button>
          </a-space>
        </template>
      </a-alert>
    </div>

    <!-- 创建快照对话框 -->
    <a-modal
      v-model:open="createDialogVisible"
      title="创建配置快照"
      @ok="handleCreate"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="快照名称" required>
          <a-input v-model:value="createForm.snapshot_name" placeholder="如：v1.0基线配置" />
        </a-form-item>
        <a-form-item label="版本号">
          <a-input v-model:value="createForm.snapshot_version" placeholder="如：v1.0.0" />
        </a-form-item>
        <a-form-item label="描述">
          <a-textarea v-model:value="createForm.description" :rows="3" />
        </a-form-item>
        <a-form-item label="设为基线">
          <a-switch v-model:checked="createForm.is_baseline" />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 快照详情对话框 -->
    <a-modal
      v-model:open="detailDialogVisible"
      title="快照详情"
      width="900px"
      :footer="null"
    >
      <div v-if="currentSnapshot">
        <a-descriptions :column="2" bordered>
          <a-descriptions-item label="快照名称" :span="2">
            {{ currentSnapshot.snapshot_name }}
          </a-descriptions-item>
          <a-descriptions-item label="类型">
            {{ getTypeText(currentSnapshot.snapshot_type) }}
          </a-descriptions-item>
          <a-descriptions-item label="版本">
            {{ currentSnapshot.snapshot_version || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="Worker">
            {{ currentSnapshot.worker_name }}
          </a-descriptions-item>
          <a-descriptions-item label="快照时间">
            {{ currentSnapshot.snapshot_time }}
          </a-descriptions-item>
        </a-descriptions>

        <a-divider>配置详情</a-divider>
        
        <a-collapse>
          <a-collapse-panel key="model" header="模型配置">
            <pre>{{ JSON.stringify(currentSnapshot.config_data?.model, null, 2) }}</pre>
          </a-collapse-panel>
          <a-collapse-panel key="prompt" header="提示词模板">
            <div>名称: {{ currentSnapshot.config_data?.prompt_template?.name }}</div>
            <div>大小: {{ currentSnapshot.config_data?.prompt_template?.content?.length }} 字符</div>
          </a-collapse-panel>
          <a-collapse-panel key="knowledge" header="知识配置">
            <div>共 {{ currentSnapshot.config_data?.knowledge?.knowledge_details?.length }} 条知识</div>
            <a-list
              :data-source="currentSnapshot.config_data?.knowledge?.knowledge_details || []"
              size="small"
            >
              <template #renderItem="{ item }">
                <a-list-item>
                  {{ item.title }} ({{ item.type_name }})
                </a-list-item>
              </template>
            </a-list>
          </a-collapse-panel>
          <a-collapse-panel key="output" header="输出参数">
            <pre>{{ JSON.stringify(currentSnapshot.config_data?.output_params, null, 2) }}</pre>
          </a-collapse-panel>
        </a-collapse>
      </div>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons-vue';
import {
  getSnapshotsByTestCase,
  createSnapshot,
  getSnapshotDetail,
  updateSnapshot,
  deleteSnapshot as deleteSnapshotAPI,
  compareSnapshots as compareSnapshotsAPI
} from '@/api/evalSnapshotV2';

const props = defineProps({
  testCaseId: {
    type: Number,
    required: true
  }
});

const snapshots = ref([]);
const loading = ref(false);
const createDialogVisible = ref(false);
const detailDialogVisible = ref(false);
const currentSnapshot = ref(null);
const compareList = ref([]);

const createForm = ref({
  snapshot_name: '',
  snapshot_version: '',
  description: '',
  is_baseline: false
});

const columns = [
  { title: '快照名称', dataIndex: 'snapshot_name', key: 'snapshot_name', width: 200 },
  { title: '类型', dataIndex: 'snapshot_type', key: 'snapshot_type', width: 100 },
  { title: '版本', dataIndex: 'snapshot_version', key: 'snapshot_version', width: 100 },
  { title: '配置概要', key: 'config_summary', width: 200 },
  { title: '快照时间', dataIndex: 'snapshot_time', key: 'snapshot_time', width: 180 },
  { title: '操作', key: 'action', width: 280, fixed: 'right' }
];

const fetchSnapshots = async () => {
  loading.value = true;
  try {
    const res = await getSnapshotsByTestCase(props.testCaseId);
    if (res.code === 200) {
      snapshots.value = res.data || [];
    }
  } catch (error) {
    message.error('获取快照列表失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

const createNewSnapshot = () => {
  createForm.value = {
    snapshot_name: '',
    snapshot_version: '',
    description: '',
    is_baseline: false
  };
  createDialogVisible.value = true;
};

const handleCreate = async () => {
  try {
    await createSnapshot({
      test_case_id: props.testCaseId,
      ...createForm.value
    });
    message.success('快照创建成功');
    createDialogVisible.value = false;
    fetchSnapshots();
  } catch (error) {
    message.error('创建快照失败：' + error.message);
  }
};

const viewDetail = async (id) => {
  try {
    const res = await getSnapshotDetail(id);
    if (res.code === 200) {
      currentSnapshot.value = res.data;
      detailDialogVisible.value = true;
    }
  } catch (error) {
    message.error('获取快照详情失败：' + error.message);
  }
};

const setAsBaseline = async (id) => {
  try {
    await updateSnapshot(id, { is_baseline: true });
    message.success('已设置为基线快照');
    fetchSnapshots();
  } catch (error) {
    message.error('设置失败：' + error.message);
  }
};

const selectForCompare = (snapshot) => {
  if (compareList.value.length < 2) {
    compareList.value.push(snapshot);
  }
};

const compareSnapshots = async () => {
  try {
    const res = await compareSnapshotsAPI(
      compareList.value[0].id,
      compareList.value[1].id
    );
    if (res.code === 200) {
      // 显示对比结果
      message.success('快照对比完成');
      console.log('对比结果:', res.data);
      // 这里可以打开一个对话框显示对比结果
    }
  } catch (error) {
    message.error('对比失败：' + error.message);
  } finally {
    compareList.value = [];
  }
};

const deleteSnapshot = async (id) => {
  try {
    await deleteSnapshotAPI(id);
    message.success('删除成功');
    fetchSnapshots();
  } catch (error) {
    message.error('删除失败：' + error.message);
  }
};

const getTypeText = (type) => {
  const texts = {
    baseline: '基线',
    latest: '最新',
    custom: '自定义'
  };
  return texts[type] || type;
};

onMounted(() => {
  fetchSnapshots();
});
</script>

<style scoped>
.snapshot-manager {
  padding: 16px;
}
</style>

